import { useSyncExternalStore } from "react";
import type { IBookmarkedFormation, IFormation } from "@/types/formation";

// Session-scoped bookmark store shared across the whole app.
//
// Mirrors lib/auth.ts: a web-storage-backed store exposed through
// useSyncExternalStore, so every consumer (the cards, the navbar counter and
// the saved-formations page) stays in sync without prop drilling or a heavier
// state library. Bookmarks live in sessionStorage — they survive a refresh
// within the same tab session and are cleared when the tab is closed.
//
// All sessionStorage access goes through this module: components never touch
// storage directly.

const STORAGE_KEY = "sf-bookmarks";
const BOOKMARKS_EVENT = "sf-bookmarks-change";

// Shared empty reference so snapshots stay referentially stable when there's
// nothing stored (required by useSyncExternalStore to avoid render loops).
const EMPTY: readonly IBookmarkedFormation[] = [];

// Parse-once cache: getSnapshot returns the same array reference until the
// underlying string actually changes, so we never re-parse sessionStorage on
// every render.
let cachedRaw: string | null = null;
let cachedList: readonly IBookmarkedFormation[] = EMPTY;

function readBookmarks(): readonly IBookmarkedFormation[] {
  if (typeof window === "undefined") return EMPTY;
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    try {
      cachedList = raw ? (JSON.parse(raw) as IBookmarkedFormation[]) : EMPTY;
    } catch {
      cachedList = EMPTY;
    }
  }
  return cachedList;
}

function writeBookmarks(list: IBookmarkedFormation[]): void {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new Event(BOOKMARKS_EVENT));
}

// ── Public helpers ──────────────────────────────────────────────────────────

/** Projects an API formation onto the lean payload we persist. */
export function toBookmark(formation: IFormation): IBookmarkedFormation {
  return {
    id: formation.id,
    slug: formation.slug,
    title: formation.title,
    description: formation.description,
    category: formation.category,
    category_slug: formation.category_slug,
    location: formation.location,
    duration: formation.duration,
    price: formation.price,
    financing: formation.financing ?? "",
    isNew: formation.isNew,
  };
}

/** Current bookmarks (stable reference until the store changes). */
export function getBookmarks(): readonly IBookmarkedFormation[] {
  return readBookmarks();
}

export function isBookmarked(slug: string): boolean {
  return readBookmarks().some((b) => b.slug === slug);
}

/** Adds a bookmark, ignoring duplicates (matched by slug). */
export function addBookmark(formation: IBookmarkedFormation): void {
  const list = readBookmarks();
  if (list.some((b) => b.slug === formation.slug)) return;
  writeBookmarks([...list, formation]);
}

export function removeBookmark(slug: string): void {
  writeBookmarks(readBookmarks().filter((b) => b.slug !== slug));
}

/** Toggles a bookmark on/off; returns the resulting saved state. */
export function toggleBookmark(formation: IBookmarkedFormation): boolean {
  const list = readBookmarks();
  if (list.some((b) => b.slug === formation.slug)) {
    writeBookmarks(list.filter((b) => b.slug !== formation.slug));
    return false;
  }
  writeBookmarks([...list, formation]);
  return true;
}

export function clearBookmarks(): void {
  writeBookmarks([]);
}

// ── Store subscription ────────────────────────────────────────────────────────

function subscribe(onChange: () => void): () => void {
  window.addEventListener(BOOKMARKS_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(BOOKMARKS_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function getServerSnapshot(): readonly IBookmarkedFormation[] {
  return EMPTY;
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/** The full list of bookmarked formations. */
export function useBookmarks(): readonly IBookmarkedFormation[] {
  return useSyncExternalStore(subscribe, readBookmarks, getServerSnapshot);
}

/** Number of bookmarks — drives the navbar counter. */
export function useBookmarkCount(): number {
  return useSyncExternalStore(
    subscribe,
    () => readBookmarks().length,
    () => 0,
  );
}

/** Whether a formation (by slug) is currently bookmarked. */
export function useIsBookmarked(slug: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => (slug ? isBookmarked(slug) : false),
    () => false,
  );
}
